import { useQuery as useTeamMembersQuery } from "@faire/web-api--source/endpoints/www/api/retailer/team-members/get-hooks";
import { trackRetailerTeamManagementMultiUserHomepageTeamModalCtaClick } from "@faire/web-api--source/events/retailerTeamManagement/click/multiUserHomepageTeamModalCta";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import myTeamRoute from "@faire/web-api--source/routes/www/retailer/settings/my-team";
import { Avatar } from "@faire/web/slate-deprecated/Avatar";
import { LoadingSpinner } from "@faire/web/slate-deprecated/LoadingSpinner";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { Modal } from "@faire/web/slate/Modal";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

interface IProps {
  onClose: () => void;
}

export const TeamMembersModal: React.FC<IProps> = ({ onClose }) => {
  const { data, isPending: isLoading } = useTeamMembersQuery();

  return (
    <StyledModal
      data-test-id="homepage-banner-team-members-modal"
      align="flex-start"
      padding="88px 24px 24px 24px"
      onClose={onClose}
      width="400px"
      height="490px"
    >
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          id="gLCvI3"
          defaultMessage="Team members"
          description={{
            text: "Title of a modal that displays a list of team members belonging to this multi user retailer",
            img: "https://cdn.faire.com/fastly/9e7c5114445fa9ed1f14d610eef2a94a6b4696e339d35c5ac09bc3645e24871a.png",
          }}
        />
      </Typography>
      <Spacer height="3x" />
      {isLoading ? (
        <LoadingSpinnerWrapper>
          <LoadingSpinner />
        </LoadingSpinnerWrapper>
      ) : (
        <TeamMembersWrapper align="flex-start">
          {(data?.team_members_data ?? []).map((teamMember, index) => (
            <TeamMemberRow
              key={`team-member-title-${teamMember.title}-${index}`}
            >
              <Avatar
                image={IImage.build({ url: teamMember.image_url })}
                size="smaller"
              />
              <Spacer size="2x" />
              <Typography variant="paragraphSansMedium">
                {teamMember.title}
              </Typography>
            </TeamMemberRow>
          ))}
        </TeamMembersWrapper>
      )}
      <Spacer height="4x" />
      <ViewTeamButton
        linkTo={myTeamRoute()}
        onClick={trackRetailerTeamManagementMultiUserHomepageTeamModalCtaClick}
      >
        <StrictLocalMsg
          id="NSWdMK"
          defaultMessage="View team page"
          description={{
            text: "Button in a modal that links to a page that displays all team members belonging to this multi user retailer",
            img: "https://cdn.faire.com/fastly/9e7c5114445fa9ed1f14d610eef2a94a6b4696e339d35c5ac09bc3645e24871a.png",
          }}
        />
      </ViewTeamButton>
    </StyledModal>
  );
};

const LoadingSpinnerWrapper = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  width: 100%;
  height: 225px;
`;

const StyledModal = styled(Modal)`
  ${responsiveMediaQueries.mobile} {
    height: auto !important;
    position: absolute !important;
    inset: auto auto 0 auto !important;
  }
`;

const TeamMembersWrapper = styled(Column)`
  width: 100%;
  overflow: auto;
  height: 225px;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    height: 0;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0);
  }
`;

const TeamMemberRow = styled(Row).attrs({ align: "center" })`
  padding: ${getSpacing("1x", 0)};
`;

const ViewTeamButton = styled(Button)`
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;
